import { UserId } from "@nimble/datamodel/types/userId";
import { Plus } from "lucide-react";
import { useState } from "react";
import { UseFormReturn, useWatch } from "react-hook-form";
import { toast } from "sonner";

import {
  FILTER_KEYS,
  FilterValues,
} from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/filters/filter-schema";
import {
  ActiveSelection,
  SavedSearch,
} from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/filters/saved-search-and-preferences/active-selection";
import { SaveSearchModal } from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/filters/saved-search-and-preferences/save-search-modal";
import { SAVE_SEARCH_ACTION_LABEL } from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/filters/saved-search-and-preferences/saved-search.constants";
import { SavedSearchesSidebarList } from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/filters/saved-search-and-preferences/saved-searches-sidebar-list";
import useActiveFilterCount from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/filters/use-active-filter-count";
import { ConfirmationModal } from "@/components/shared/dialogs/confirmation-modal/confirmation-modal";
import { useDeleteSavedSearchMutation } from "@/hooks/data/use-save-searches-mutation";

const SAVE_BUTTON_LABEL: Record<ActiveSelection["kind"], string> = {
  preferences: "These match your preferences",
  savedSearch: "These are already saved",
  none: SAVE_SEARCH_ACTION_LABEL,
};

type ModalState =
  | { kind: "save" }
  | { kind: "delete"; search: SavedSearch }
  | null;

export const SavedSearches = ({
  userId,
  form,
  filterDefaults,
  activeSelection,
}: {
  userId: UserId;
  form: UseFormReturn<FilterValues>;
  filterDefaults: FilterValues;
  activeSelection: ActiveSelection;
}) => {
  const [modal, setModal] = useState<ModalState>(null);
  const currentFilters = useWatch({ control: form.control });
  const activeFilterCount = useActiveFilterCount(
    form,
    FILTER_KEYS,
    filterDefaults,
  );

  const alreadySaved = activeSelection.kind !== "none";
  const hasNoFilters = activeFilterCount === 0;

  const { mutate: deleteSearch, isPending: isDeleting } =
    useDeleteSavedSearchMutation(userId);

  const closeModal = () => setModal(null);

  const handleConfirmDelete = () => {
    if (modal?.kind !== "delete") return;
    const deletedUuid = modal.search.uuid;
    deleteSearch(deletedUuid, {
      onSuccess: () => {
        if (
          activeSelection.kind === "savedSearch" &&
          activeSelection.uuid === deletedUuid
        ) {
          form.reset(filterDefaults);
        }
        toast.success("Search deleted");
        closeModal();
      },
      onError: () => {
        toast.error("Could not delete the saved search");
      },
    });
  };

  return (
    <>
      <SavedSearchesSidebarList
        filterDefaults={filterDefaults}
        form={form}
        userId={userId}
        activeSelection={activeSelection}
        onDelete={(search) => setModal({ kind: "delete", search })}
      />
      <button
        type="button"
        disabled={alreadySaved || hasNoFilters}
        onClick={() => setModal({ kind: "save" })}
        data-testid="saved-filters-save-current"
        className="mt-2 -mx-1 inline-flex items-center gap-1.5 py-1.5 px-2 text-xs font-medium rounded-md transition-colors bg-surface-subtle hover:bg-line-faint disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-surface-subtle"
      >
        <Plus className="size-3.5" />
        {SAVE_BUTTON_LABEL[activeSelection.kind]}
      </button>
      {modal?.kind === "save" && (
        <SaveSearchModal
          open
          onClose={closeModal}
          filters={currentFilters}
          userId={userId}
        />
      )}
      {modal?.kind === "delete" && (
        <ConfirmationModal
          title={`Remove "${modal.search.name}"?`}
          description="Your saved filters will be deleted. You can always set up a new saved search from the Discover page."
          onPrimaryClick={handleConfirmDelete}
          onSecondaryClick={closeModal}
          primaryLabel={"Yes, remove it"}
          secondaryLabel={"Keep it"}
          isPrimaryDisabled={isDeleting}
        />
      )}
    </>
  );
};
